import {useCallback} from 'react';
import {Button, Link} from '@ezcater/tapas';
import {
  faEnvelope,
  faMessage,
  faMobile,
  faPhone,
  faSquareExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import useEzCaterCompanyInfo from '@/hooks/useEzCaterCompanyInfo';
import {useHydrated} from '@/hooks/useHydrated';
import useIdentity from '@/hooks/useIdentity';
import useSelfServiceReportingModal from '@/hooks/useSelfServiceReportingModal';
import useTracking from '@/hooks/useTracking';
import useWebchatEntryPoint from '@/hooks/useWebchatEntryPoint';
import {PRIVACY_PATH, TERMS_PATH} from '@/paths';
import {gtm} from '@/utils';
import {contactUsClickPayload} from '@/utils/googleAnalyticsEvents';
import trackChatStarted from '@/utils/trackChatStarted';
import {useSupportMessageEnabled} from '../SupportMessage';
import ContactChannel from './ContactChannel';

type ContactChannelsProps = {
  onClose?: () => void;
  onStartSupportMessage?: () => void;
  className?: string;
  experimentVariation: string;
  variant?: 'modal' | 'page';
  showHeading?: boolean;
};

const actionClassName = 'text-base font-bold text-peppercorn-800 underline underline-offset-2';

const disclaimerLinkClassName = 'font-bold italic text-peppercorn-600 no-underline';

const externalLink = {target: '_blank', rel: 'noopener noreferrer'} as const;

const ContactChannels: React.FC<ContactChannelsProps> = ({
  onClose,
  onStartSupportMessage,
  className,
  experimentVariation,
  variant = 'modal',
  showHeading = true,
}) => {
  const surface = variant === 'page' ? 'contact_us_page' : 'contact_us_modal';
  const {t} = useTranslation('common');
  const {track} = useTracking();
  const {openWebchat, isMobileWebchat, webchatHref} = useWebchatEntryPoint();
  const companyInfo = useEzCaterCompanyInfo();
  const {isLoggedIn} = useIdentity();
  const {toggleSelfServiceProviderModal} = useSelfServiceReportingModal();
  const showSupportMessageWizard = useSupportMessageEnabled();
  // CSP+Cloudflare prevents us from rendering `mailto:` links in our initial HTML
  const canRenderEmailLink = useHydrated();

  const trackMethodSelected = useCallback(
    (methodType: string) => {
      gtm.dataLayer(
        contactUsClickPayload({contactType: methodType === 'sms' ? 'text' : methodType}),
      );
      track('clicked-contact-us-link', {
        page: window.location.pathname,
        misc_json: JSON.stringify({
          method: methodType,
          variant,
          experiment_variation: experimentVariation,
        }),
      });
    },
    [track, experimentVariation, variant],
  );

  const startChat = useCallback(() => {
    trackChatStarted();
    trackMethodSelected('chat');
    openWebchat();
    onClose?.();
  }, [trackMethodSelected, openWebchat, onClose]);

  const startSelfServiceReport = useCallback(() => {
    track('self-service-reporting-modal-opened', {page: window.location.pathname});
    trackMethodSelected('self-service');
    toggleSelfServiceProviderModal();
    onClose?.();
  }, [track, trackMethodSelected, toggleSelfServiceProviderModal, onClose]);

  let messageAction: React.ReactNode;
  if (showSupportMessageWizard) {
    messageAction = (
      <Button
        className={twMerge(actionClassName, 'p-0')}
        onClick={() => {
          trackMethodSelected('support-message');
          track('support-message-modal-opened', {
            page: window.location.pathname,
            misc_json: JSON.stringify({
              is_logged_in: isLoggedIn,
              surface,
            }),
          });
          onStartSupportMessage?.();
        }}
        size="small"
        variant="buttonLink"
      >
        {t('components.SupportMessage.button')}
      </Button>
    );
  } else if (canRenderEmailLink) {
    messageAction = (
      <Link
        className={actionClassName}
        href={`mailto:${companyInfo.supportEmailAddress}`}
        onClick={() => trackMethodSelected('email')}
      >
        {t('components.SupportMessage.button')}
      </Link>
    );
  } else {
    messageAction = <div className="h-5 w-40 animate-pulse rounded-md bg-peppercorn-200" />;
  }

  return (
    <div className={twMerge('flex flex-col gap-12', className)} data-testid="contact-channels">
      <section className="flex flex-col gap-8">
        {showHeading && (
          <h2 className="text-xl font-bold text-peppercorn-800 tablet:-mb-2">
            {t('components.ContactUs.channels.liveSupport')}
          </h2>
        )}

        <ContactChannel
          icon={faMessage}
          description={t('components.ContactUs.channels.chatDescription')}
          recommendedLabel={t('components.ContactUs.channels.recommended')}
          action={
            isMobileWebchat ? (
              <Link
                className={actionClassName}
                href={webchatHref}
                onClick={() => {
                  trackChatStarted();
                  trackMethodSelected('chat');
                  onClose?.();
                }}
                rel="noopener noreferrer"
                target="_blank"
              >
                {t('components.ContactUs.info.chat')}
              </Link>
            ) : (
              <Button
                className={twMerge(actionClassName, 'p-0')}
                onClick={startChat}
                size="small"
                variant="buttonLink"
              >
                {t('components.ContactUs.info.chat')}
              </Button>
            )
          }
        />

        <ContactChannel
          icon={faPhone}
          description={t('components.ContactUs.channels.phoneDescription')}
          action={
            <Link
              className={actionClassName}
              href={`tel:${companyInfo.supportPhoneNumber.replace(/[^0-9]/g, '')}`}
              onClick={() => trackMethodSelected('phone')}
            >
              <span className="tablet:hidden">
                {t('components.ContactUs.channels.callLabel', {
                  number: companyInfo.supportPhoneNumber,
                })}
              </span>
              <span className="hidden tablet:inline">{companyInfo.supportPhoneNumber}</span>
            </Link>
          }
        />

        {/* Text is mobile-only per the PRD; gated in CSS so the server and first paint agree. */}
        <ContactChannel
          className="tablet:hidden"
          icon={faMobile}
          description={t('components.ContactUs.channels.textDescription')}
          action={
            <Link
              className={actionClassName}
              href={`sms:${companyInfo.supportSmsNumber}`}
              onClick={() => trackMethodSelected('sms')}
            >
              {t('components.ContactUs.channels.textLabel', {
                number: companyInfo.supportSmsDisplayNumber,
              })}
            </Link>
          }
        />

        <ContactChannel
          icon={faEnvelope}
          description={t('components.ContactUs.channels.sendMessageDescription')}
          action={messageAction}
        />
      </section>

      {isLoggedIn && (
        <section className="flex flex-col gap-6">
          <h2 className="text-xl font-bold text-peppercorn-800">
            {t('components.ContactUs.channels.orderIssues')}
          </h2>

          <ContactChannel
            icon={faSquareExclamation}
            description={t('components.ContactUs.channels.reportAnIssueDescription')}
            action={
              <Button
                className={twMerge(actionClassName, 'p-0')}
                onClick={startSelfServiceReport}
                size="small"
                variant="buttonLink"
              >
                {t('components.ContactUs.info.reportAnIssue')}
              </Button>
            }
          />
        </section>
      )}

      <p className="text-xs italic leading-snug text-peppercorn-600 tablet:hidden">
        <Trans
          i18nKey="common:components.ContactUs.info.smsConsentDisclaimer"
          components={{
            PrivacyLink: (
              <Link className={disclaimerLinkClassName} href={PRIVACY_PATH} {...externalLink} />
            ),
            TermsLink: (
              <Link className={disclaimerLinkClassName} href={TERMS_PATH} {...externalLink} />
            ),
            SmsTermsLink: (
              <Link
                className={disclaimerLinkClassName}
                href="https://ezcater.com/sms-terms"
                {...externalLink}
              />
            ),
          }}
        />
      </p>
    </div>
  );
};

export default ContactChannels;
